import { ChannelIcon, ErrorState, Loading, PageHead, useFormat } from '@ly360/ui';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import type { TaskRow } from '../crm/data';
import { TaskList } from '../crm/TaskList';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

interface Dashboard {
  counts: Record<string, number>;
  pipeline: { currency: string; deals: number; amountMinor: string; weightedMinor: string }[];
  wonThisMonth: { currency: string; deals: number; amountMinor: string }[];
  recentConversations: {
    id: string;
    channel: string;
    lastMessageAt: string | null;
    personName: string;
    personId: string;
  }[];
}

function Stat({
  label,
  value,
  note,
  to,
  tone,
}: {
  label: string;
  value: string;
  note?: string;
  to: string;
  tone?: 'warn';
}) {
  return (
    <Link to={to} className="stat" style={{ display: 'block' }}>
      <div className="label">{label}</div>
      <div className="value">{value}</div>
      {note && (
        <div
          className={tone === 'warn' ? 'overdue' : 'faint'}
          style={{ fontSize: 'var(--t-xs)', marginTop: 2 }}
        >
          {note}
        </div>
      )}
    </Link>
  );
}

export function HomePage() {
  const { t, i18n } = useTranslation();
  const { session } = useAuth();
  const fmt = useFormat();
  const q = useQuery({ queryKey: ['dashboard'], queryFn: () => api<Dashboard>('/v1/dashboard') });
  const tasks = useQuery({
    queryKey: ['tasks', 'home'],
    queryFn: () => api<{ items: TaskRow[] }>('/v1/tasks?assignee=me&status=open'),
  });
  const ar = i18n.language === 'ar' || i18n.language === 'ur';
  const name = (ar && session?.user.displayNameAr) || session?.user.displayName.split(' ')[0] || '';
  if (q.isLoading) return <Loading />;
  if (q.isError || !q.data) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const c = q.data.counts;
  const n = (v: number | undefined) => fmt.number(v ?? 0);

  return (
    <>
      <PageHead
        eyebrow={t('nav.home')}
        title={t('crm.home.greeting', { name })}
        subtitle={t('crm.home.subtitle')}
      />
      <div className="stat-grid" style={{ marginBottom: '1rem' }} data-testid="home-stats">
        <Stat
          label={t('crm.home.openConversations')}
          value={n(c.openConversations)}
          note={`${t('crm.home.unassigned')}: ${n(c.unassignedConversations)}`}
          to="/inbox"
        />
        <Stat
          label={t('crm.home.openCases')}
          value={n(c.openCases)}
          note={`${t('crm.home.urgentCases')}: ${n(c.urgentCases)}`}
          to="/cases"
          tone={c.urgentCases ? 'warn' : undefined}
        />
        <Stat
          label={t('crm.home.myTasks')}
          value={n(c.myOpenTasks)}
          note={`${t('crm.home.overdue')}: ${n(c.myOverdueTasks)}`}
          to="/tasks"
          tone={c.myOverdueTasks ? 'warn' : undefined}
        />
        <Stat label={t('crm.home.people')} value={n(c.people)} to="/people" />
        <Stat label={t('crm.home.companies')} value={n(c.accounts)} to="/companies" />
      </div>
      <div className="record-grid">
        <div style={{ display: 'grid', gap: '1rem', minWidth: 0 }}>
          <section className="card">
            <div className="card-head">
              <h2>{t('crm.home.myTaskList')}</h2>
              <Link to="/tasks" className="btn btn-ghost btn-sm">
                {t('common.viewAll')}
              </Link>
            </div>
            {tasks.data &&
              (tasks.data.items.length ? (
                <TaskList items={tasks.data.items.slice(0, 8)} />
              ) : (
                <p className="empty">{t('crm.home.noTasks')}</p>
              ))}
          </section>
          <section className="card">
            <div className="card-head">
              <h2>{t('crm.home.recentConversations')}</h2>
              <Link to="/inbox" className="btn btn-ghost btn-sm">
                {t('common.viewAll')}
              </Link>
            </div>
            {q.data.recentConversations.length ? (
              <ul className="list-plain">
                {q.data.recentConversations.map((rc) => (
                  <li key={rc.id}>
                    <span className="channel-badge">
                      <ChannelIcon channel={rc.channel} />
                    </span>
                    <Link to={`/people/${rc.personId}`} className="grow" dir="auto">
                      <strong>{rc.personName}</strong>
                    </Link>
                    <span className="faint" style={{ fontSize: 'var(--t-xs)' }}>
                      {rc.lastMessageAt ? fmt.relative(rc.lastMessageAt) : ''}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="empty">{t('crm.home.noConversations')}</p>
            )}
          </section>
        </div>
        <aside className="panel-dark card-pad" aria-label={t('crm.home.pipeline')}>
          <div className="eyebrow" style={{ color: 'var(--tx-on-dark-faint)' }}>
            {t('crm.home.pipeline')}
          </div>
          {q.data.pipeline.length === 0 && <p className="mute">—</p>}
          {q.data.pipeline.map((p) => (
            <div key={p.currency} className="pref-row" style={{ borderColor: 'var(--line-dark-soft)' }}>
              <span className="preview-val" style={{ fontSize: '1.6rem' }}>
                {fmt.money(Number(p.amountMinor), p.currency)}
              </span>
              <span className="mute" style={{ fontSize: 'var(--t-xs)' }}>
                {t('crm.home.dealsCount', { count: p.deals })} ·{' '}
                {t('crm.home.weighted', { value: fmt.money(Number(p.weightedMinor), p.currency) })}
              </span>
            </div>
          ))}
          <div className="eyebrow" style={{ color: 'var(--tx-on-dark-faint)', marginTop: '1rem' }}>
            {t('crm.home.wonThisMonth')}
          </div>
          {q.data.wonThisMonth.length === 0 && <p className="mute">—</p>}
          {q.data.wonThisMonth.map((w) => (
            <div key={w.currency} className="pref-row" style={{ borderColor: 'var(--line-dark-soft)' }}>
              <span className="preview-val">{fmt.money(Number(w.amountMinor), w.currency)}</span>
              <span className="mute" style={{ fontSize: 'var(--t-xs)' }}>
                {t('crm.home.dealsCount', { count: w.deals })}
              </span>
            </div>
          ))}
          <Link to="/deals" className="btn btn-light btn-sm" style={{ marginTop: '1rem' }}>
            {t('nav.deals')}
          </Link>
        </aside>
      </div>
    </>
  );
}
